import { JSONPatchOperation } from './json-patch'

export function transformSDKResponse(response: any): any {
  if (Array.isArray(response)) {
    return response.map(transformSDKResponse)
  } else if (typeof response === 'object' && response !== null) {
    return Object.fromEntries(
      Object.entries(response).map(([key, value]) => [
        transformKey(key),
        shouldPreserveCasing(key) ? value : transformSDKResponse(value)
      ])
    )
  } else {
    return response
  }
}

function transformKey(key: string): string {
  if (key.indexOf('-') !== -1) {
    // we dont want to mess with uuids
    return key
  }
  return camelize(key)
}

const SNAKE_CASE_KEYS = [
  'inputs',
  'core_inputs',
  'prev_inputs',
  'prior_inputs',
  'upload_headers',
  'form_config'
]

function transformPointerKey(key: string, ancestors: ReadonlyArray<string>): string {
  if (key.indexOf('-') !== -1) {
    // we dont want to mess with uuids
    return key
  }
  if (SNAKE_CASE_KEYS.some((k) => ancestors.includes(k))) {
    return key
  }
  return camelize(key)
}

function shouldPreserveCasing(key: string): boolean {
  return SNAKE_CASE_KEYS.includes(key)
}

export function transformJsonPatch(
  patch: ReadonlyArray<JSONPatchOperation>
): ReadonlyArray<JSONPatchOperation> {
  return patch.map((op) => {
    switch (op.op) {
      case 'add':
        return {
          op: 'add',
          path: transformJsonPointer(op.path, transformPointerKey),
          value: transformSDKResponse(op.value)
        }
      case 'remove':
        return {
          op: 'remove',
          path: transformJsonPointer(op.path, transformPointerKey)
        }
      case 'replace':
        return {
          op: 'replace',
          path: transformJsonPointer(op.path, transformPointerKey),
          value: transformSDKResponse(op.value)
        }
      case 'test':
        return {
          op: 'test',
          path: transformJsonPointer(op.path, transformPointerKey),
          value: transformSDKResponse(op.value)
        }
      case 'move':
        return {
          op: 'move',
          from: transformJsonPointer(op.from, transformPointerKey),
          path: transformJsonPointer(op.path, transformPointerKey)
        }
      case 'copy':
        return {
          op: 'copy',
          from: transformJsonPointer(op.from, transformPointerKey),
          path: transformJsonPointer(op.path, transformPointerKey)
        }
      default:
        return op
    }
  })
}

function camelize(str: string): string {
  return str
    .split(/[_-]/g)
    .map((part, idx) => (idx == 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)))
    .join('')
}

// RFC 6901 JSON Pointer helpers

export function transformJsonPointer(
  pointer: string,
  transformToken: (key: string, ancestors: ReadonlyArray<string>) => string
): string {
  if (pointer === '') return ''
  // Split on '/'. Leading '/' yields first empty segment; preserve leading slash in join
  const parts = pointer.split('/')
  const head = parts.shift() // '' if pointer started with '/'

  const ancestors: string[] = []

  const transformed = parts.map((raw) => {
    const decoded = decodePointerSegment(raw)
    const ancestorsForThisToken = ancestors.slice()
    const output = isArrayIndexSegment(decoded)
      ? raw
      : encodePointerSegment(transformToken(decoded, ancestorsForThisToken))
    ancestors.push(decoded)
    return output
  })
  return [head ?? '', ...transformed].join('/')
}

function decodePointerSegment(segment: string): string {
  return segment.replace(/~1/g, '/').replace(/~0/g, '~')
}

function encodePointerSegment(segment: string): string {
  return segment.replace(/~/g, '~0').replace(/\//g, '~1')
}

function isArrayIndexSegment(segment: string): boolean {
  // numeric index (0, 1, 2, ...). '-' is handled separately as append; treat it as non-camelizable too
  return /^(0|[1-9]\d*)$/.test(segment) || segment === '-'
}
